import React from 'react';
import { Modal, Typography, Alert, Space, Tag, Spin, Button } from 'antd';
import {
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

export interface StageSyncProgress {
  stage: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error' | 'skipped';
  message?: string;
  durationMs?: number;
}

interface StageSyncProgressModalProps {
  visible: boolean;
  onClose: () => void;
  stages: StageSyncProgress[];
  isLoading: boolean;
  syncComplete: boolean;
  totalDurationMs?: number;
  errors?: Array<{ stage: string; error: string }>;
  userPrompt?: string;
  onRetryStage?: (stage: string) => void;
}

const stageDisplayNames: Record<string, string> = {
  requirements: 'Requirements',
  jiraStories: 'Jira Stories',
  'diagram:High-Level System Design': 'High-Level System Design',
  'diagram:Data Flow Diagram': 'Data Flow Diagram',
  'diagram:Component Diagram': 'Component Diagram',
  'diagram:API Schema': 'API Schema',
  database: 'Database Schema',
  fullstack: 'Fullstack Code',
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'in_progress':
      return <LoadingOutlined style={{ color: '#1890ff' }} spin />;
    case 'error':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    case 'skipped':
      return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    default:
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed': return 'success';
    case 'in_progress': return 'processing';
    case 'error': return 'error';
    case 'skipped': return 'default';
    default: return 'warning';
  }
};

const StageSyncProgressModal: React.FC<StageSyncProgressModalProps> = ({
  visible,
  onClose,
  stages,
  isLoading,
  syncComplete,
  totalDurationMs,
  errors,
  userPrompt,
  onRetryStage,
}) => {
  return (
    <Modal
      title={
        <Space>
          <SyncOutlined spin={isLoading} />
          <span>{isLoading ? 'Syncing Project Stages...' : syncComplete ? 'Sync Complete' : 'Stage Sync'}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={syncComplete ? (
        <button
          onClick={onClose}
          style={{
            padding: '6px 16px',
            backgroundColor: '#1890ff',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Close & Review Stages
        </button>
      ) : null}
      closable={!isLoading}
      maskClosable={!isLoading}
      width={560}
    >
      {userPrompt && (
        <Alert
          message="Enhancement Being Added"
          description={userPrompt}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ padding: '8px 0' }}>
        {stages.map((stage, index) => (
          <div
            key={stage.stage}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 8px',
              borderBottom: index < stages.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            <div style={{ marginRight: 12 }}>{getStatusIcon(stage.status)}</div>
            <div style={{ flex: 1 }}>
              <Text strong>{stageDisplayNames[stage.stage] || stage.stage}</Text>
              {stage.message && (
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {stage.message}
                  </Text>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color={getStatusColor(stage.status)}>
                {stage.status === 'in_progress' ? 'Updating...' : stage.status}
              </Tag>
              {stage.status === 'error' && onRetryStage && (
                <Button
                  size="small"
                  onClick={() => onRetryStage(stage.stage)}
                  disabled={isLoading}
                >
                  Retry
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {syncComplete && totalDurationMs && (
        <Alert
          message={errors && errors.length > 0 ? 'Sync Completed with Errors' : 'Sync Completed Successfully'}
          description={
            <div>
              <Text>
                Stages updated: {stages.filter(s => s.status === 'completed').length} / {stages.length}
              </Text>
              {errors && errors.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {errors.map((e, i) => (
                    <div key={i}>
                      <Text type="danger">
                        {stageDisplayNames[e.stage] || e.stage}: {e.error}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
              <br />
              <Text strong style={{ marginTop: 8, display: 'block' }}>
                Please review and approve all updated stages.
              </Text>
            </div>
          }
          type={errors && errors.length > 0 ? 'warning' : 'success'}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default StageSyncProgressModal;
